import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthHook } from '../../hooks/useAuth.hook';
import { Routes } from '../../types/enums/Routes.enum';
import { Loading } from '../Loading';
import { storage } from '../../utils/Const.utils';
import { StorageKeys } from '../../types/enums/Storage.enum';

interface PublicRouteProps {
    children: React.ReactNode;
    redirectTo?: string;
    showLoadingWhileChecking?: boolean;
}

/**
 * Componente que protege rutas públicas, redirigiendo usuarios autenticados
 * al dashboard o ruta especificada
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
    children,
    redirectTo = Routes.DEFAULT,
    showLoadingWhileChecking = true
}) => {
    const { isAuthenticated, isLoading } = useAuthHook();
    const location = useLocation();

    /* Mostrar loading mientras verifica autenticación */
    if (isLoading) {
        // Siempre mostrar loading si está cargando para evitar parpadeo
        if (showLoadingWhileChecking) {
            return (
                <Loading
                    variant="linear"
                    message="Verificando estado de sesión..."
                    fullscreen
                    showIcon
                />
            );
        }
        // Si no se debe mostrar loading, retornar null para evitar render parcial
        return null;
    }

    // Si el usuario está autenticado, redirigir según el estado de selección de perfil
    if (isAuthenticated) {
        /* Verificar si el usuario completó la selección de rol/perfil */
        const hasCompletedRolSelection = storage.get(StorageKeys.PERFIL);
        
        /* Si no ha completado la selección de perfil, redirigir a POST_LOGIN_ROL */
        if (!hasCompletedRolSelection) {
            return (
                <Navigate
                    to={Routes.POST_LOGIN_ROL}
                    replace
                    state={{ from: location.state?.from || redirectTo }}
                />
            );
        }

        // Si ya completó la selección de perfil, redirigir a la ruta de destino
        const from: string = (location.state as any)?.from || redirectTo;

        return (
            <Navigate
                to={from}
                replace
            />
        );
    }

    // Si no está autenticado, mostrar el contenido público
    return <>{children}</>;
};

/**
 * HOC (Componente de Orden Superior) para envolver componentes con PublicRoute
 */
export const withPublicRoute = <P extends object>(
    Component: React.ComponentType<P>,
    options?: {
        redirectTo?: string;
        showLoadingWhileChecking?: boolean;
    }
) => {
    const WrappedComponent = (props: P) => {
        return (
            <PublicRoute
                redirectTo={options?.redirectTo}
                showLoadingWhileChecking={options?.showLoadingWhileChecking}
            >
                <Component {...props} />
            </PublicRoute>
        );
    };

    WrappedComponent.displayName = `withPublicRoute(${Component.displayName || Component.name})`;
    return WrappedComponent;
};

export default PublicRoute;